export const ROLES = {
  SUPER_ADMIN: "superadmin",
  RESELLER_ADMIN: "reselleradmin",
  CLIENT_ADMIN: "clientadmin",
  CLIENT_PRODUCT_MANAGER: "clientproductmanager",
  CLIENT_USER_MANAGER: "clientusermanager",
  CLIENT_ORDER_APPROVER: "clientorderapprover",
  CUSTOMER: "customer",
};

const R = ROLES;
const ADMIN = [R.SUPER_ADMIN, R.RESELLER_ADMIN];
const ALL_STAFF = [
  ...ADMIN,
  R.CLIENT_ADMIN,
  R.CLIENT_PRODUCT_MANAGER,
  R.CLIENT_USER_MANAGER,
  R.CLIENT_ORDER_APPROVER,
];
const ALL_ROLES = [...ALL_STAFF, R.CUSTOMER];

// Reads role from JWT payload if available (tamper-proof); falls back to localStorage.role.
// NOTE: Strapi v3 standard JWTs don't include role by default. If the backend adds it,
// this function will use it automatically — making the route guards tamper-resistant.
export function getRoleFromToken() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return localStorage.getItem("role");
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    return payload.role?.type ?? localStorage.getItem("role");
  } catch {
    return localStorage.getItem("role");
  }
}

// Ordered from most-specific to least-specific — matchPath uses first match
export const ROUTE_PERMISSIONS = [
  // /app/* — Reseller-level (superadmin + reselleradmin only)
  { path: "/app/products",                            exact: true, roles: ADMIN },
  { path: "/app/add-product",                         exact: true, roles: ADMIN },
  { path: "/app/product/:id",                         exact: true, roles: ADMIN },
  { path: "/app/product-attributes",                  exact: true, roles: ADMIN },
  { path: "/app/product-attribute/:id",               exact: true, roles: [...ADMIN, R.CLIENT_ADMIN, R.CLIENT_PRODUCT_MANAGER] },
  { path: "/app/attributes/templates",                exact: true, roles: ADMIN },
  { path: "/app/attributes/template/new",             exact: true, roles: ADMIN },
  { path: "/app/attributes/template/:id",             exact: true, roles: ADMIN },
  { path: "/app/users",                               exact: true, roles: ADMIN },
  { path: "/app/users-points",                        exact: true, roles: ADMIN },
  { path: "/app/reports",                             exact: true, roles: ADMIN },
  { path: "/app/orders",                              exact: true, roles: ALL_STAFF },
  { path: "/app/orders/:order_id",                    exact: true, roles: ALL_STAFF },
  // /app/client/* — Client-level (more specific paths first)
  { path: "/app/client/:id/products/assign/:pid",     exact: true, roles: [...ADMIN, R.CLIENT_ADMIN, R.CLIENT_PRODUCT_MANAGER] },
  { path: "/app/client/:id/products/assign",          exact: true, roles: [...ADMIN, R.CLIENT_ADMIN, R.CLIENT_PRODUCT_MANAGER] },
  { path: "/app/client/:id/products",                 exact: true, roles: [...ADMIN, R.CLIENT_ADMIN, R.CLIENT_PRODUCT_MANAGER] },
  { path: "/app/client/:id/users",                    exact: true, roles: [...ADMIN, R.CLIENT_ADMIN, R.CLIENT_PRODUCT_MANAGER, R.CLIENT_USER_MANAGER] },
  { path: "/app/client/:id/approval",                 exact: true, roles: ALL_STAFF },
  { path: "/app/client/:id/store/products/user/:uid", exact: true, roles: ALL_STAFF },
  { path: "/app/client/:id/store/products",           exact: true, roles: ALL_STAFF },
  { path: "/app/client/:id/points_log/:lid",          exact: true, roles: ALL_STAFF },
  { path: "/app/client/:id/points_log",               exact: true, roles: ALL_STAFF },
  { path: "/app/store/checkout",                      exact: true, roles: ALL_STAFF },
  { path: "/app/client/:id",                          exact: true, roles: [...ADMIN, R.CLIENT_ADMIN] },
  // User profile (all roles)
  { path: "/app/client/:c/user/:uid",                 exact: true, roles: ALL_ROLES },
  { path: "/app/user/:id",                            exact: true, roles: ALL_ROLES },
  { path: "/app/user",                                exact: true, roles: ALL_ROLES },
  // /store/* — Customer store (customer only — other roles use /app equivalents)
  { path: "/store/products",                          exact: true, roles: [R.CUSTOMER] },
  { path: "/store/orders/:order_id",                  exact: true, roles: [R.CUSTOMER] },
  { path: "/store/orders",                            exact: true, roles: [R.CUSTOMER] },
  { path: "/store/checkout",                          exact: true, roles: [R.CUSTOMER] },
  { path: "/store/user",                              exact: true, roles: [R.CUSTOMER] },
];

// Ownership scopes for data-level access checks
export const SCOPE = {
  GLOBAL:   "global",   // No restriction
  RESELLER: "reseller", // Must match own reseller_id
  CLIENT:   "client",   // Must match own client_id
  OWN:      "own",      // Must match own user ID
};

// Default redirect path per role when ownership check fails
export const DEFAULT_REDIRECT = {
  superadmin:           ()         => "/app/products",
  reselleradmin:        ()         => "/app/products",
  clientadmin:          (clientId) => `/app/client/${clientId}`,
  clientproductmanager: (clientId) => `/app/client/${clientId}/products`,
  clientusermanager:    (clientId) => `/app/client/${clientId}/users`,
  clientorderapprover:  (clientId) => `/app/client/${clientId}/approval`,
  customer:             ()         => "/store/products",
};

// Data ownership matrix: role → resource → scope.
// To add a new resource (e.g. "products"), add a column here and use checkOwnership() in the component.
export const OWNERSHIP = {
  superadmin:           { profile: SCOPE.GLOBAL,   orders: SCOPE.GLOBAL },
  reselleradmin:        { profile: SCOPE.RESELLER, orders: SCOPE.RESELLER, products: SCOPE.RESELLER },
  clientadmin:          { profile: SCOPE.CLIENT,   orders: SCOPE.CLIENT,   client: SCOPE.CLIENT, products: SCOPE.CLIENT },
  clientproductmanager: { profile: SCOPE.CLIENT,   orders: SCOPE.CLIENT,   products: SCOPE.CLIENT },
  clientusermanager:    { profile: SCOPE.CLIENT,   orders: SCOPE.CLIENT },
  clientorderapprover:  { profile: SCOPE.CLIENT,   orders: SCOPE.CLIENT },
  customer:             { profile: SCOPE.OWN,      orders: SCOPE.OWN },
};

/**
 * Check data-level ownership for a loaded resource.
 * Returns true if access was denied and redirect was triggered; false if access is allowed.
 *
 * @param {string} role - Current user's role (from localStorage)
 * @param {string} resource - Key in OWNERSHIP matrix ("profile", "orders")
 * @param {object} ids - { ownerId, resellerId, clientId, clientIds }
 * @param {object} history - React Router history object
 */
export function checkOwnership(role, resource, { ownerId, resellerId, resellerIds, clientId, clientIds } = {}, history) {
  const scope = OWNERSHIP[role]?.[resource];
  if (!scope || scope === SCOPE.GLOBAL) return false;

  const myId       = localStorage.getItem("user");
  const myClientId = localStorage.getItem("client_id");
  const myReseller = localStorage.getItem("reseller_id");
  const redirect   = DEFAULT_REDIRECT[role]?.(myClientId) ?? "/";

  let blocked = false;
  if (scope === SCOPE.OWN) {
    blocked = String(ownerId) !== String(myId);
  } else if (scope === SCOPE.RESELLER) {
    const ids = resellerIds ? resellerIds.map(String) : [String(resellerId)];
    blocked = !ids.includes(String(myReseller));
  } else if (scope === SCOPE.CLIENT) {
    const ids = clientIds ? clientIds.map(String) : [String(clientId)];
    blocked = !ids.includes(String(myClientId));
  }

  if (blocked) history.push(redirect);
  return blocked;
}

/**
 * Redirects to the caller's default section when a profile fetch returned no
 * user (the backend denied access and GraphQL nulled the field). Returns
 * true if a redirect was triggered (caller should bail out), false if a
 * user is present and the caller should proceed to its own ownership check.
 *
 * @param {object|null} user - The fetched target user, or null/undefined if denied
 * @param {string} role - Current user's role
 * @param {string} clientId - Current user's active client id
 * @param {object} history - React Router history object
 */
export function redirectIfNoUser(user, role, clientId, history) {
  if (user) return false;
  history.push(DEFAULT_REDIRECT[role]?.(clientId) ?? "/");
  return true;
}
